import dayjs from 'dayjs';
import { normalizeTextToWinSettings } from 'src/modules/campaigns/helpers';
import { TextToWinCampaignsParams, TextToWinSettings } from 'src/modules/campaigns/types';
import { convertHtmlToText } from 'src/helpers/helpers';

export const validateTextToWinSettingsOrder = (
  settings: TextToWinSettings,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const start = settings.dateStart ? dayjs(settings.dateStart) : null;
  const end = settings.dateEnd ? dayjs(settings.dateEnd) : null;
  const draw = settings.dateDraw ? dayjs(settings.dateDraw) : null;
  const resultsSend = settings.dateResultsSend ? dayjs(settings.dateResultsSend) : null;

  if (start?.isValid() && end?.isValid() && !end.isAfter(start)) {
    errors['textToWinSettings.dateEnd'] = 'End date must be after the start date.';
  }

  if (end?.isValid() && draw?.isValid() && !draw.isAfter(end)) {
    errors['textToWinSettings.dateDraw'] = 'Draw date must be after the end date.';
  }

  if (draw?.isValid() && resultsSend?.isValid() && !resultsSend.isAfter(draw)) {
    errors['textToWinSettings.dateResultsSend'] =
      'Results send date must be after the draw date.';
  }

  return errors;
};

export const validateTextToWinSettings = (
  settings: TextToWinSettings,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!settings.dateStart) {
    errors['textToWinSettings.dateStart'] = 'Start date is required.';
  }
  if (!settings.dateEnd) {
    errors['textToWinSettings.dateEnd'] = 'End date is required.';
  }
  if (!settings.dateDraw) {
    errors['textToWinSettings.dateDraw'] = 'Draw date is required.';
  }
  if (!settings.dateResultsSend) {
    errors['textToWinSettings.dateResultsSend'] = 'Results send date is required.';
  }

  if (settings.entryIntervalMinutes === null || settings.entryIntervalMinutes <= 0) {
    errors['textToWinSettings.entryIntervalMinutes'] =
      'Entry interval minutes must be at least 1.';
  }
  if (settings.maxEntriesPerInterval === null || settings.maxEntriesPerInterval <= 0) {
    errors['textToWinSettings.maxEntriesPerInterval'] =
      'Max entries per interval must be at least 1.';
  }
  if (settings.maxEntriesPerContact === null || settings.maxEntriesPerContact <= 0) {
    errors['textToWinSettings.maxEntriesPerContact'] =
      'Max entries per contact must be at least 1.';
  }
  if (settings.winnersCount === null || settings.winnersCount < 1) {
    errors['textToWinSettings.winnersCount'] = 'Winners count must be at least 1.';
  }

  if (!convertHtmlToText(settings.entrySuccessMessageText)) {
    errors['textToWinSettings.entrySuccessMessageText'] = 'Entry success message is required.';
  }
  if (!convertHtmlToText(settings.winnerMessageText)) {
    errors['textToWinSettings.winnerMessageText'] = 'Winner message is required.';
  }

  return {
    ...errors,
    ...validateTextToWinSettingsOrder(settings),
  };
};

const cleanMessageText = (text: string | null): string | null => {
  if (text === null) {
    return null;
  }
  const plain = convertHtmlToText(text);
  return plain ? plain : null;
};

export const buildTextToWinPayloadFields = (
  settings: TextToWinSettings,
): Omit<
  TextToWinCampaignsParams,
  'campaignType' | 'externalType' | 'status' | 'configId' | 'name' | 'optOutText' | 'keywords'
> => {
  const normalized = normalizeTextToWinSettings(settings);

  return {
    dateStart: normalized.dateStart,
    dateEnd: normalized.dateEnd,
    dateDraw: normalized.dateDraw,
    dateResultsSend: normalized.dateResultsSend,
    entryIntervalMinutes: normalized.entryIntervalMinutes,
    maxEntriesPerInterval: normalized.maxEntriesPerInterval,
    maxEntriesPerContact: normalized.maxEntriesPerContact,
    winnersCount: normalized.winnersCount ?? 1,
    entrySuccessMessageText: convertHtmlToText(normalized.entrySuccessMessageText) || '',
    entryIntervalLimitMessageText: cleanMessageText(normalized.entryIntervalLimitMessageText),
    entryTotalLimitMessageText: cleanMessageText(normalized.entryTotalLimitMessageText),
    entryNotStartedMessageText: cleanMessageText(normalized.entryNotStartedMessageText),
    entryEndedMessageText: cleanMessageText(normalized.entryEndedMessageText),
    winnerMessageText: convertHtmlToText(normalized.winnerMessageText) || '',
    nonWinnerMessageText: cleanMessageText(normalized.nonWinnerMessageText),
  };
};

export const getTextToWinMessageTexts = (settings: TextToWinSettings): string[] =>
  [
    settings.entrySuccessMessageText,
    settings.entryIntervalLimitMessageText,
    settings.entryTotalLimitMessageText,
    settings.entryNotStartedMessageText,
    settings.entryEndedMessageText,
    settings.winnerMessageText,
    settings.nonWinnerMessageText,
  ]
    .map(text => convertHtmlToText(text || ''))
    .filter(Boolean);
